import './Promise.ext';
import { DayOfWeek } from '@Common/Enums/Enum';

declare global {
    interface HTMLElement {
        insertAfter(newElement: HTMLElement, refElement: HTMLElement): HTMLElement;

        replaceLastChild(newChild: HTMLElement): HTMLElement;

        remove(): HTMLElement | void;

        isFirstChild(): boolean;

        isLastChild(): boolean;

        appendChildren(elements: HTMLElement[]): HTMLElement;
    }

    interface String {
        startsWith(prefix: string): boolean;

        endsWith(suffix: string): boolean;

        replaceAll(search: string, replace: string): string;

        format(...args: any[]): string;

        isWhitespaceOrEmpty(): boolean;

        insertAt(index: number, value: string): string;

        contains(str: string): boolean;

        getMatches(regex: RegExp): string[];
    }

    interface StringConstructor {
        format(format: string, ...args: any[]): string;

        isWhitespaceOrEmpty(str: string): boolean;
    }

    interface Array<T> {
        move(from: number, to: number): void;

        remove<T>(item: T, comparer?: (item1: T, item2: T) => boolean): void;

        remove<T, K>(item: K, comparer?: (item1: K, item2: T) => boolean): void;

        removeAt(from: number, to?: number): Array<T>;

        equals(array: T[], comparer?: (item1: T, item2: T) => boolean): boolean;

        contains(item: T): boolean;

        contains(comparer: (item: T) => boolean): boolean;

        addRange(items: T[]): void;

        containsAll(array: T[]): boolean;

        repeat(count: number): any[]; // add generic
        getIndexOf(comparer: (T) => boolean): number;

        stableSort(comparer: (item1: T, item2: T) => number): T[];

        insert(index: number, element: T): void;

        firstOrNull(comparer: (item: T) => boolean): T;

        searchTree(getKey?: (obj: T) => string): IMap<T>;

        searchTree<U>(getKey: (obj: T) => string, getValue?: (obj: T) => U): IMap<U>;

        searchTree<U>(getKey: (obj: T) => string, getValue?: (obj: T, index: number) => U): IMap<U>;
    }

    interface Date {
        format(format?: string): string;

        getDayOfWeek(): string;

        setAbsoluteYear(year: number, month: number): void;
    }

    interface DateConstructor {
        format(date: Date, format?: string): string;
    }

    interface Number {
        format(format: string): string;

        hasFlag(flag: number): boolean;

        roundTo(base: number): number;
    }

    interface NumberConstructor {
        isNumber(arg: any): arg is number;

        isInteger(arg: any): arg is number;
    }

    interface FunctionConstructor {
        isFunction(arg: any): arg is Function;

        isFunction<T extends Function>(arg: any): arg is T;

        getName(fn: Function): string;
    }

    interface SVGElement {
        hasClass(className: string): boolean;

        addClass(className: string): void;

        removeClass(className: string): void;

        toggleClass(className: string, condition: boolean): void;
    }
}

HTMLElement.prototype.insertAfter = function (newElement, refElement) {
    if (refElement.nextSibling) {
        this.insertBefore(newElement, refElement.nextSibling);
    } else {
        this.appendChild(newElement);
    }

    return newElement;
};

HTMLElement.prototype.replaceLastChild = function (newChild) {
    this.children[this.children.length - 1] = newChild;
    return this;
};

HTMLElement.prototype.remove = function () {
    this.parentNode.removeChild(this);
    return this;
};

HTMLElement.prototype.isFirstChild = function () {
    return this.parentNode.firstChild === this;
};

HTMLElement.prototype.isLastChild = function () {
    return this.parentNode.lastChild === this;
};

HTMLElement.prototype.appendChildren = function (elements) {
    for (let i = 0; i < elements.length; i++) {
        this.appendChild(elements[i]);
    }

    return this;
};

String.prototype.startsWith = function (prefix: string) {
    return this.indexOf(prefix) === 0;
};
String.prototype.endsWith = function (suffix: string) {
    return this.substr(-suffix.length) === suffix;
};
String.prototype.replaceAll = function (search: string, replace: string) {
    return this.split(search).join(replace);
};

String.prototype.format = function (...args: any[]) {
    // call via prototype for iframes
    return this.replace(/\{\{|\}\}|\{(\d+)\}/g, (m, n) => {
        if (m === '{{') {
            return '{';
        }
        if (m === '}}') {
            return '}';
        }
        return args[n];
    });
};

String.prototype.isWhitespaceOrEmpty = function () {
    return this.length === 0 || this.trim().length === 0;
};
String.prototype.insertAt = function (index, value) {
    return [this.slice(0, index), value, this.slice(index)].join('');
};

String.format = function (arg: string, ...args: any[]) {
    return arg.format(...args);
};
String.isWhitespaceOrEmpty = function (arg) {
    return arg.isWhitespaceOrEmpty();
};
if (!String.prototype.contains) {
    String.prototype.contains = function (str: string) {
        return this.indexOf(str) !== -1;
    };
}

String.prototype.getMatches = function (regexp: RegExp) {
    const result = [];
    let m;

    while ((m = regexp.exec(this)) != null) {
        if (m.index === regexp.lastIndex) {
            regexp.lastIndex++;
        }
        result.push(m[1]);
    }

    return result;
};

Date.prototype.setAbsoluteYear = function (year: number, month: number) {
    const oldMonth = this.getMonth();
    this.setYear(year);
    if (this.getMonth() !== oldMonth) {
        this.setHours(this.getHours() - 24);
    }
    this.setMonth(month);
    while (this.getMonth() !== month) {
        this.setHours(this.getHours() - 24);
    }
};

const nativeGetTimezoneOffset = Date.prototype.getTimezoneOffset;
Date.prototype.getTimezoneOffset = function (): number {
    return Math.round(nativeGetTimezoneOffset.apply(this));
};

Date.prototype.getDayOfWeek = function () {
    const dw = DayOfWeek;
    const day = [dw.sunday, dw.monday, dw.tuesday, dw.wednesday, dw.thursday, dw.friday, dw.saturday][this.getDay()];
    return dw.getName(day);
};

Date.format = function (date: Date, format?: string) {
    return date.format(format);
};

Number.prototype.hasFlag = function (flag: number) {
    return (this & flag) === flag;
};

Number.prototype.roundTo = function (base: number) {
    const mod = this % base;
    if (mod <= base / 2) {
        return this - mod;
    } else {
        return this + base - mod;
    }
};

Number.isNumber = (arg: any): arg is number => typeof arg === 'number';
Number.isInteger = Number.isInteger || ((value: number): value is number => Number.isNumber(value) && isFinite(value) && Math.floor(value) === value);

Object.defineProperty(Array.prototype, 'move', {
    enumerable: false,
    value: function (from: number, to: number) {
        this.splice(to, 0, this.splice(from, 1)[0]);
    }
});

Object.defineProperty(Array.prototype, 'remove', {
    enumerable: false,
    value: function (item, comparer?) {
        const startIndex = Function.isFunction(comparer) ? _.findIndex(this, _.partial(comparer, item)) : this.indexOf(item);
        if (startIndex !== -1) {
            this.splice(startIndex, 1);
        }
    }
});

Object.defineProperty(Array.prototype, 'removeAt', {
    enumerable: false,
    value: function (from, to) {
        const rest = this.slice((to || from) + 1 || this.length);
        this.length = from < 0 ? this.length + from : from;
        return this.push(...rest);
    }
});

Object.defineProperty(Array.prototype, 'equals', {
    enumerable: false,
    value: function (array: any[], comparer) {
        return _.isEqualWith(this, array, comparer);
    }
});

Object.defineProperty(Array.prototype, 'contains', {
    enumerable: false,
    value: function (obj) {
        return Function.isFunction(obj) ? _.some(this, obj) : this.indexOf(obj) >= 0;
    }
});

Object.defineProperty(Array.prototype, 'addRange', {
    enumerable: false,
    value: function (items: any[]) {
        this.push(...items);
    }
});

Object.defineProperty(Array.prototype, 'containsAll', {
    enumerable: false,
    value: function (array: any[]) {
        return _.difference(array, this).length === 0;
    }
});

Object.defineProperty(Array.prototype, 'repeat', {
    enumerable: false,
    value: function (count) {
        const newArray = [];
        for (let i = 0; i < count; i++) {
            newArray.push(this[i % this.length]);
        }
        return newArray;
    }
});

Object.defineProperty(Array.prototype, 'stableSort', {
    enumerable: false,
    value: function (comparer) {
        const newArray = this.map((item, index) => ({ index, item }));
        newArray.sort((a, b) => {
            const sortValue = comparer(a.item, b.item);
            return sortValue === 0 ? a.index - b.index : sortValue;
        });

        return newArray.map(item => item.item);
    }
});

Object.defineProperty(Array.prototype, 'getIndexOf', {
    enumerable: false,
    value: function (comparer) {
        return _.findIndex(this, comparer);
    }
});

Object.defineProperty(Array.prototype, 'insert', {
    enumerable: false,
    value: function (index, element) {
        this.splice(index, 0, element);
        return this;
    }
});

Object.defineProperty(Array.prototype, 'firstOrNull', {
    enumerable: false,
    value: function (comparer) {
        return _.find(this, comparer);
    }
});

Object.defineProperty(Array.prototype, 'searchTree', {
    enumerable: false,
    value: function (getKey, getValue?) {
        const obj = {};
        getValue = getValue || _.identity;
        getKey = getKey || (item => item.id);
        for (let i = 0; i < this.length; i++) {
            obj[getKey(this[i])] = getValue(this[i], i);
        }
        return obj;
    }
});

Function.isFunction = (arg): arg is Function => typeof arg === 'function';
Function.getName = (fn: Function): string => {
    function functionName(fn: Function) {
        const result = /^function\s+([\w$]+)\s*\(/.exec(fn.toString());
        return result ? result[1] : '';
    }

    return fn['name'] || functionName(fn);
};

(function () {
    function Empty() {}

    if (!Function.prototype.bind) {
        Function.prototype.bind = function bind(that) {
            const target = this;
            if (typeof target !== 'function') {
                throw new TypeError('Function.prototype.bind called on incompatible ' + target);
            }
            const args = Array.prototype.slice.call(arguments, 1);
            const bound = function () {
                if (this instanceof bound) {
                    const result = target.apply(this, args.concat(Array.prototype.slice.call(arguments)));
                    if (Object(result) === result) {
                        return result;
                    }
                    return this;
                } else {
                    return target.apply(that, args.concat(Array.prototype.slice.call(arguments)));
                }
            };
            if (target.prototype) {
                Empty.prototype = target.prototype;
                bound.prototype = new Empty();
                Empty.prototype = null;
            }
            return bound;
        };
    }
})();

if (!(window.console && window.console.log)) {
    const consoleName: string = 'console';
    window[consoleName] = {
        log: function () {},
        debug: function () {},
        info: function () {},
        warn: function () {},
        error: function () {}
    };
}

if (SVGElement) {
    SVGElement.prototype.hasClass = function (className) {
        return new RegExp('(\\s|^)' + className + '(\\s|$)').test(this.getAttribute('class'));
    };

    SVGElement.prototype.addClass = function (className) {
        if (!this.hasClass(className)) {
            this.setAttribute('class', this.getAttribute('class') + ' ' + className);
        }
    };

    SVGElement.prototype.removeClass = function (className) {
        const removedClass = this.getAttribute('class').replace(new RegExp('(\\s|^)' + className + '(\\s|$)', 'g'), '$2');
        if (this.hasClass(className)) {
            this.setAttribute('class', removedClass);
        }
    };

    SVGElement.prototype.toggleClass = function (className, condition) {
        if (condition !== undefined ? !condition : this.hasClass(className)) {
            this.removeClass(className);
        } else {
            this.addClass(className);
        }
    };
}

export function extend(base, inherited): void {
    inherited.prototype = new base();
    inherited.prototype.constructor = inherited;
}

interface Map<K, V> {
    // avoid read from map by index
    readonly [key: string]: unknown;
}

declare global {
    interface ObjectConstructor {
        /**
         * return true if value is non-primitive types (null, undefined, literals)
         * @param value can be null
         */
        isObject(value: any): value is object | GenericFunction;

        /**
         * returns own and inherited (non- and enumerable) property names
         * @param value can be null
         */
        keysAll(value: any): string[];
    }
}

const isObjectTypeof = ['object', 'function'];

Object.isObject = function (value: any): value is object | GenericFunction {
    return value != null && isObjectTypeof.includes(typeof value);
};

const keysAll = function (value: any): Set<string> {
    const result = new Set<string>();
    while (value) {
        Object.getOwnPropertyNames(value).forEach(p => result.add(p));
        value = Object.getPrototypeOf(value);
    }
    return result;
};
const baseValues = keysAll({});
Object.keysAll = function (value: any): string[] {
    const keys = keysAll(value);
    baseValues.forEach(v => keys.delete(v));
    return [...keys];
};
